'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { X, ChevronLeft, ChevronRight, Info, Minimize2 } from 'lucide-react';
import type { Gallery } from '@/types/gallery';
import { useLocale, useTranslations } from 'next-intl';
import { localeMetadata, routing, type AppLocale } from "@/i18n/routing";

interface FullscreenGalleryModalProps {
  gallery: Gallery | null;
  isOpen: boolean;
  onClose: () => void;
  initialImageIndex?: number;
}

export function FullscreenGalleryModal({
  gallery,
  isOpen,
  onClose,
  initialImageIndex = 0,
}: FullscreenGalleryModalProps) {
  const locale = useLocale();
  const t = useTranslations();
  const tGallery = useTranslations('common.gallery');
  const currentLocale =
    localeMetadata[locale as AppLocale] ??
    localeMetadata[routing.defaultLocale as AppLocale];
  
  const [selectedImage, setSelectedImage] = useState<number>(initialImageIndex);
  const [isInfoExpanded, setIsInfoExpanded] = useState(false);
  const [mounted, setMounted] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  // Track if component is mounted (for portal SSR safety)
  useEffect(() => {
    setMounted(true);
  }, []);

  // Reset state when gallery changes or modal opens
  useEffect(() => {
    if (isOpen && gallery) {
      setSelectedImage(initialImageIndex);
      setIsInfoExpanded(false);
    }
  }, [isOpen, gallery, initialImageIndex]);

  // Navigation functions
  const nextImage = useCallback(() => {
    if (gallery && gallery.images.length > 1) {
      setSelectedImage((prev) => (prev + 1) % gallery.images.length);
    }
  }, [gallery]);

  const prevImage = useCallback(() => {
    if (gallery && gallery.images.length > 1) {
      setSelectedImage((prev) => (prev === 0 ? gallery.images.length - 1 : prev - 1));
    }
  }, [gallery]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'ArrowRight':
          nextImage();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        case 'Escape':
          onClose();
          break;
      }
    },
    [isOpen, nextImage, prevImage, onClose]
  );

  // Touch swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50; // Minimum swipe distance
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swipe left - next image
        nextImage();
      } else {
        // Swipe right - previous image
        prevImage();
      }
    }
    
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Add/remove event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  // Don't render if not open, no gallery, or not mounted (SSR)
  if (!mounted || !isOpen || !gallery || !gallery.images.length) {
    return null;
  }

  const currentImage = gallery.images[selectedImage];
  const hasMultipleImages = gallery.images.length > 1;
  const showThumbnails = gallery.images.length > 2;
  
  // Resolve translations
  const galleryTitle = gallery.name ? t(gallery.name) : '';
  const galleryDescription = gallery.description ? t(gallery.description) : '';
  const imageTitle = currentImage.name ? t(currentImage.name) : '';
  const imageDescription = currentImage.description ? t(currentImage.description) : '';
  
  // Use image-specific title/description if available, otherwise gallery's
  const displayTitle = imageTitle || galleryTitle;
  const displayDescription = imageDescription || galleryDescription;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}
      role="dialog"
      aria-modal="true"
      aria-label={displayTitle}
    >
      {/* Top bar with photo count and close button */}
      <div className="absolute inset-x-4 top-4 z-20 flex items-center justify-between">
        
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white"
          aria-label={tGallery('close')}
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Main image area */}
      <div
        className="relative flex-1 flex items-center justify-center px-16 py-16 md:px-20"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Main image */}
        <div className="relative h-full w-full max-h-[70vh] md:max-h-[80vh]">
          <Image
            src={currentImage.src}
            alt={displayTitle}
            fill
            sizes="(max-width: 768px) 100vw, 80vw"
            className="object-contain"
            priority
          />
        </div>
      </div>

      {/* Bottom info bar */}
      <div className="relative z-10 bg-gradient-to-t from-black/80 to-transparent">
        {/* Minimized info bar */}
        {!isInfoExpanded && (
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex-1 min-w-0">
              <h3 className="truncate text-start font-medium text-white">{displayTitle}</h3>
            </div>
            <div className="flex items-center gap-3">
              {/* Image counter */}
              {hasMultipleImages && (
                <span className="text-white/80 text-sm">
                  {selectedImage + 1} / {gallery.images.length}
                </span>
              )}
              {/* Expand button - only if there's a description */}
              {displayDescription && (
                <button
                  type="button"
                  onClick={() => setIsInfoExpanded(true)}
                  className="cursor-pointer rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white"
                  aria-label={tGallery("show_more_info")}
                >
                  <Info className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Expanded info panel */}
        {isInfoExpanded && (
          <div className="space-y-3 px-4 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-start text-lg font-semibold text-white">{displayTitle}</h3>
                {displayDescription && (
                  <p className="mt-2 text-start text-sm text-white/80">{displayDescription}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsInfoExpanded(false)}
                className="cursor-pointer shrink-0 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white"
                aria-label={tGallery("minimize_info")}
              >
                <Minimize2 className="h-5 w-5" />
              </button>
            </div>
            {/* Image counter in expanded view */}
            {hasMultipleImages && (
              <span className="text-white/60 text-sm">
                {selectedImage + 1} / {gallery.images.length}
              </span>
            )}
          </div>
        )}

        {/* Thumbnail strip - only show if more than 2 images */}
        {showThumbnails && (
          <div className="px-4 pb-4 overflow-x-auto">
            <div className="flex gap-2 justify-center">
              {gallery.images.map((image, index) => (
                <button
                  type="button"
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`cursor-pointer relative h-12 w-16 shrink-0 overflow-hidden rounded border-2 transition-all focus:outline-none focus:ring-2 focus:ring-white ${
                    index === selectedImage
                      ? 'border-white'
                      : 'border-transparent opacity-60 hover:opacity-80'
                  }`}
                  aria-label={tGallery("go_to_image", { index: index + 1 })}
                  aria-current={index === selectedImage ? 'true' : 'false'}
                >
                  <Image
                    src={image.src}
                    alt={tGallery("thumbnail_alt", { index: index + 1 })}
                    fill
                    sizes="64px"
                    className="object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Navigation arrows - rendered last to ensure they're on top */}
      {hasMultipleImages && (
        <>
          <button
            type="button"
            onClick={prevImage}
            className="fixed start-4 top-1/2 z-[100] -translate-y-1/2 cursor-pointer rounded-full bg-black/50 p-3 text-white transition-colors hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white md:start-6"
            aria-label={tGallery('previous')}
          >
            {currentLocale.isRtl ? (
              <ChevronRight className="h-6 w-6 md:h-8 md:w-8" />
            ) : (
              <ChevronLeft className="h-6 w-6 md:h-8 md:w-8" />
            )}
          </button>
          <button
            type="button"
            onClick={nextImage}
            className="fixed end-4 top-1/2 z-[100] -translate-y-1/2 cursor-pointer rounded-full bg-black/50 p-3 text-white transition-colors hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white md:end-6"
            aria-label={tGallery('next')}
          >
            {currentLocale.isRtl ? (
              <ChevronLeft className="h-6 w-6 md:h-8 md:w-8" />
            ) : (
              <ChevronRight className="h-6 w-6 md:h-8 md:w-8" />
            )}
          </button>
        </>
      )}
    </div>,
    document.body
  );
}
